'use client';
import React, { Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ReceiptData {
  bookingId: string;
  consultantName: string;
  consultantInitials: string;
  consultantTitle: string;
  consultantCategory: string;
  consultantEmail: string;
  consultantPhone: string;
  sessionDate: string;
  sessionTime: string;
  sessionDuration: string;
  sessionTopic: string;
  sessionFee: number;
  platformFee: number;
  totalPaid: number;
  currency: string;
  meetingLink: string;
  threadId: string;
  paidAt: string;
}

// ─── Mock receipt data (in production, derive from query params / DB) ─────────
const mockReceipt: ReceiptData = {
  bookingId: 'BK-2026-07-0047',
  consultantName: 'Yusuf Al-Rashid',
  consultantInitials: 'YR',
  consultantTitle: 'Islamic Finance & Sukuk Structuring Expert',
  consultantCategory: 'Islamic Finance',
  consultantEmail: 'yusuf.alrashid@rafeeq.com',
  consultantPhone: '+60 12-345 6789',
  sessionDate: 'Wednesday, 9 July 2026',
  sessionTime: '10:00 AM – 11:00 AM (GMT+8)',
  sessionDuration: '60 min',
  sessionTopic: 'Sukuk Structuring for SME Expansion',
  sessionFee: 150,
  platformFee: 7.5,
  totalPaid: 157.5,
  currency: 'USD',
  meetingLink: 'https://meet.google.com/abc-defg-hij',
  threadId: 'thread-bk-0047',
  paidAt: '3 July 2026, 4:19 PM',
};

function generateCalendarLink(receipt: ReceiptData): string {
  const title = encodeURIComponent(`Consultation: ${receipt.sessionTopic}`);
  const details = encodeURIComponent(
    `Booking ID: ${receipt.bookingId}\nConsultant: ${receipt.consultantName}\nMeeting: ${receipt.meetingLink}`
  );
  const location = encodeURIComponent(receipt.meetingLink);
  // Approximate start/end (static for mock)
  const start = '20260709T020000Z';
  const end = '20260709T030000Z';
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`;
}

// ─── Receipt Inner ─────────────────────────────────────────────────────────────
function OrderReceiptInner() {
  const searchParams = useSearchParams();

  // In production: pull bookingId from query param and fetch from DB
  const bookingIdParam = searchParams.get('bookingId');
  const receipt: ReceiptData = { ...mockReceipt, bookingId: bookingIdParam || mockReceipt.bookingId };

  const calendarLink = generateCalendarLink(receipt);

  return (
    <AppLayout activeRoute="/order-receipt">
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b border-border bg-card px-6 py-4">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <Link
              href="/my-bookings"
              className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground"
            >
              <Icon name="ArrowLeftIcon" className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-lg font-bold text-foreground">Booking Receipt</h1>
              <p className="text-xs text-muted-foreground">Payment confirmed · {receipt.paidAt}</p>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-8 space-y-5">

          {/* ── Success Banner ─────────────────────────────────────────────── */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon name="CheckCircleIcon" className="w-7 h-7 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-emerald-800 text-base">Payment Successful</p>
              <p className="text-emerald-700 text-sm mt-0.5">
                Your session has been confirmed. Check the details below and add it to your calendar.
              </p>
            </div>
          </div>

          {/* ── Booking ID ─────────────────────────────────────────────────── */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-1">Booking ID</p>
                <p className="text-2xl font-bold text-foreground tracking-wider">{receipt.bookingId}</p>
              </div>
              <div className="w-12 h-12 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-center">
                <Icon name="ReceiptRefundIcon" className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Save this ID for your records and support requests.</p>
          </div>

          {/* ── Session Details ────────────────────────────────────────────── */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <Icon name="CalendarDaysIcon" className="w-4 h-4 text-primary" />
              <h2 className="font-bold text-foreground text-sm">Session Details</h2>
            </div>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Date &amp; Time</p>
                <p className="text-sm font-semibold text-foreground">{receipt.sessionDate}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{receipt.sessionTime}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Duration</p>
                <p className="text-sm font-semibold text-foreground">{receipt.sessionDuration}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground mb-1">Topic</p>
                <p className="text-sm font-semibold text-foreground">{receipt.sessionTopic}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground mb-1">Meeting Link</p>
                <a
                  href={receipt.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary hover:underline break-all"
                >
                  {receipt.meetingLink}
                </a>
              </div>
            </div>
          </div>

          {/* ── Consultant Contact ─────────────────────────────────────────── */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <Icon name="UserCircleIcon" className="w-4 h-4 text-primary" />
              <h2 className="font-bold text-foreground text-sm">Consultant Contact</h2>
            </div>
            <div className="p-5 flex items-start gap-4">
              {/* Avatar */}
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-base">{receipt.consultantInitials}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-foreground">{receipt.consultantName}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{receipt.consultantTitle}</p>
                <span className="inline-block mt-1.5 text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5 font-medium">
                  {receipt.consultantCategory}
                </span>
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Icon name="EnvelopeIcon" className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <a
                      href={`mailto:${receipt.consultantEmail}`}
                      className="text-xs text-primary hover:underline"
                    >
                      {receipt.consultantEmail}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="PhoneIcon" className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs text-foreground">{receipt.consultantPhone}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Payment Summary ────────────────────────────────────────────── */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <Icon name="CreditCardIcon" className="w-4 h-4 text-primary" />
              <h2 className="font-bold text-foreground text-sm">Payment Summary</h2>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Session fee ({receipt.sessionDuration})</span>
                <span className="font-medium text-foreground">${receipt.sessionFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Platform fee (5%)</span>
                <span className="font-medium text-foreground">${receipt.platformFee.toFixed(2)}</span>
              </div>
              <div className="border-t border-border pt-3 flex justify-between">
                <span className="font-bold text-foreground">Total Paid</span>
                <span className="font-bold text-foreground text-base">${receipt.totalPaid.toFixed(2)} {receipt.currency}</span>
              </div>
            </div>
          </div>

          {/* ── Action Cards ───────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Calendar Invite */}
            <a
              href={calendarLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-card border border-border hover:border-primary/40 rounded-2xl p-5 flex items-start gap-4 transition-all hover:shadow-md"
            >
              <div className="w-11 h-11 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                <Icon name="CalendarIcon" className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-foreground text-sm">Add to Calendar</p>
                <p className="text-xs text-muted-foreground mt-0.5">Save the session to Google Calendar with all details pre-filled.</p>
                <div className="flex items-center gap-1 mt-2 text-xs text-blue-600 font-medium">
                  <span>Add invite</span>
                  <Icon name="ArrowTopRightOnSquareIcon" className="w-3 h-3" />
                </div>
              </div>
            </a>

            {/* Message Thread */}
            <Link
              href={`/consultation-messages?thread=${receipt.threadId}`}
              className="group bg-card border border-border hover:border-primary/40 rounded-2xl p-5 flex items-start gap-4 transition-all hover:shadow-md"
            >
              <div className="w-11 h-11 bg-violet-50 border border-violet-200 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-violet-100 transition-colors">
                <Icon name="ChatBubbleLeftEllipsisIcon" className="w-5 h-5 text-violet-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-foreground text-sm">Message Thread</p>
                <p className="text-xs text-muted-foreground mt-0.5">Send pre-session notes or questions directly to your consultant.</p>
                <div className="flex items-center gap-1 mt-2 text-xs text-violet-600 font-medium">
                  <span>Open thread</span>
                  <Icon name="ArrowRightIcon" className="w-3 h-3" />
                </div>
              </div>
            </Link>
          </div>

          {/* ── Footer Actions ─────────────────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Link
              href="/my-bookings"
              className="flex-1 py-3 px-5 bg-card border border-border text-foreground font-semibold text-sm rounded-xl hover:bg-muted transition-colors text-center"
            >
              View All Bookings
            </Link>
            <Link
              href="/browse-consultants"
              className="flex-1 py-3 px-5 bg-primary text-primary-foreground font-semibold text-sm rounded-xl hover:opacity-90 transition-opacity text-center"
            >
              Book Another Session
            </Link>
          </div>

          <p className="text-center text-xs text-muted-foreground pb-4">
            A confirmation email has been sent to your registered address. For support, reference booking ID <span className="font-semibold">{receipt.bookingId}</span>.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function OrderReceiptPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <OrderReceiptInner />
    </Suspense>
  );
}
